// Vorschlaege beim Tippen (ST-SEA-002, „PQL S2b“; CM-SEA-012, CM-SEA-013):
// - im Wert von `stichwort:` fragt die Liste die Wertquelle S0;
// - im Wert von `rolle:` und `status:` kommen die Werte aus dem Code;
// - ein Feldname ab drei eindeutigen Zeichen oder auf Pfeil runter;
// - ODER nur dort, wo S1 es annimmt (Werte eines Feldes mit mehreren Werten);
// - „Meintest du?“ als erste Option statt eines Hinweises unter dem Feld.
// Hier steht nur die reine Logik -- welches Wort am Cursor steht und was eine
// Uebernahme einsetzt --, damit eine Probe sie ohne Browser prueft.

import { ROLE_LABELS, roleLabelSpellings } from '../context-roles.ts';
import { LIFECYCLE_LABELS } from '../parties.ts';
import type { Span } from './abfrage/baum.ts';
import { printValue } from './abfrage/drucker.ts';
import { STATUS_CODES, felder, fieldByName, fold, isLaterField, similarFieldName, type FieldDefinition, type FieldKey } from './abfrage/felder.ts';
import type { TermSuggestion } from './aufloesung.ts';

export interface WertAnfrage {
  /** Was nach dem Doppelpunkt schon steht, ohne oeffnendes Anfuehrungszeichen. */
  readonly praefix: string;
  /** Die Stelle des Werts, die eine Uebernahme ersetzt. */
  readonly wert: Span;
}

const LEERRAUM = /\s/u;

/**
 * Die Anfrage an S0 fuer die Stelle `cursor`, oder `undefined`, wenn dort
 * kein Wert einer Angabe `stichwort:` mit mindestens einem Zeichen steht.
 * Ein Wert in Anfuehrungszeichen darf Leerzeichen tragen.
 */
export function wertAnfrageAn(text: string, cursor: number): WertAnfrage | undefined {
  // Der Anfang des Worts: ruckwaerts bis Leerraum ausserhalb von Anfuehrungszeichen.
  let anfang = cursor;
  while (anfang > 0 && !LEERRAUM.test(text[anfang - 1] ?? '')) anfang -= 1;
  let wort = text.slice(anfang, cursor);
  // `stichwort:"Presse B|`: das Wort beginnt vor dem Leerzeichen im Anfuehrungszeichen.
  if (!wort.includes(':')) {
    const vorher = text.slice(0, cursor);
    const offen = vorher.lastIndexOf(':"');
    if (offen >= 0 && !vorher.slice(offen + 2).includes('"')) {
      let start = offen;
      while (start > 0 && !LEERRAUM.test(text[start - 1] ?? '')) start -= 1;
      anfang = start;
      wort = text.slice(anfang, cursor);
    }
  }
  const ohneNicht = wort.startsWith('-') ? wort.slice(1) : wort;
  const doppelpunkt = ohneNicht.indexOf(':');
  let wertAnfang: number;
  if (doppelpunkt > 0) {
    if (fieldByName(ohneNicht.slice(0, doppelpunkt))?.key !== 'stichwort') return undefined;
    wertAnfang = anfang + (wort.length - ohneNicht.length) + doppelpunkt + 1;
  } else {
    // `Stichwort: Lyrik` ist dieselbe Angabe wie `stichwort:Lyrik` (M-02): der
    // Feldname steht dann vor dem Leerraum (Codex-Review an #574).
    let k = anfang;
    while (k > 0 && LEERRAUM.test(text[k - 1] ?? '')) k -= 1;
    if (doppelpunkt === 0 || k === anfang || text[k - 1] !== ':') return undefined;
    let start = k - 1;
    while (start > 0 && !LEERRAUM.test(text[start - 1] ?? '')) start -= 1;
    if (fieldByName(text.slice(start, k - 1).replace(/^-/u, ''))?.key !== 'stichwort') return undefined;
    wertAnfang = anfang;
  }
  const roh = text.slice(wertAnfang, cursor);
  if (roh.startsWith('(')) return undefined;
  // Hinter einem geschlossenen Wert (`stichwort:"Presse"|`) gibt es nichts zu ergaenzen;
  // sonst reichte die Ersetzung bis ans Textende (Codex-Review an #574).
  if (roh.startsWith('"') && roh.slice(1).includes('"')) return undefined;
  const praefix = roh.startsWith('"') ? roh.slice(1) : roh;
  if (praefix.trim().length === 0) return undefined;
  // Das Ende des Werts: bis Leerraum, in Anfuehrungszeichen bis zum schliessenden.
  let ende = cursor;
  if (roh.startsWith('"')) {
    const zu = text.indexOf('"', cursor);
    ende = zu < 0 ? text.length : zu + 1;
  } else {
    while (ende < text.length && !LEERRAUM.test(text[ende] ?? '')) ende += 1;
  }
  return { praefix, wert: { from: wertAnfang, to: ende } };
}

/**
 * Setzt den gewaehlten Begriff an die Stelle des Werts. Gibt es denselben
 * Namen auf mehreren Achsen, nennt der Wert die Achse wie der Chip
 * („Presse (Branche)“); dahinter ein Leerzeichen fuer die naechste Angabe.
 */
export function vorschlagUebernehmen(
  text: string,
  anfrage: WertAnfrage,
  vorschlag: TermSuggestion,
  alle: readonly TermSuggestion[],
): { readonly text: string; readonly cursor: number } {
  const mehrdeutig = alle.some((anderer) => anderer.taxonomyTermId !== vorschlag.taxonomyTermId && anderer.displayName === vorschlag.displayName);
  const wert = printValue(mehrdeutig ? `${vorschlag.displayName} (${vorschlag.taxonomyDisplayName})` : vorschlag.displayName);
  const davor = text.slice(0, anfrage.wert.from) + wert;
  const danach = text.slice(anfrage.wert.to);
  const trenner = danach.startsWith(' ') ? '' : ' ';
  return { text: davor + trenner + danach, cursor: davor.length + 1 };
}

/** Was eine Option beim Uebernehmen einsetzt: `text` an die Stelle `stelle`. */
export interface Ersetzung {
  readonly stelle: Span;
  readonly text: string;
  /** So viele Zeichen vor dem Ende des eingesetzten Texts steht danach der Cursor. */
  readonly cursorVorEnde?: number;
}

export interface LokaleOption {
  /** Stabil je Lage; daraus wird die DOM-id. */
  readonly id: string;
  readonly titel: string;
  /** Zweite Spalte: Feldbezeichnung, Achse oder „Meintest du das?“. */
  readonly zusatz?: string;
  readonly ersetzung: Ersetzung;
}

export type VorschlagLage =
  | { readonly art: 'stichwort'; readonly anfrage: WertAnfrage }
  | { readonly art: 'liste'; readonly optionen: readonly LokaleOption[] };

const MEINTEST = 'Meintest du das?';

/** Ab so vielen Zeichen oeffnen Feldvorschlaege von selbst (DESIGN P2-3). */
export const FELD_MINDESTLAENGE = 3;

/** Felder mit Werten aus dem Code; `stichwort` fragt S0, die uebrigen sind offen. */
const LOKALE_WERTE: Partial<Record<FieldKey, readonly { readonly code: string; readonly label: string; readonly schreibweisen: readonly string[] }[]>> = {
  rolle: Object.entries(ROLE_LABELS).map(([code, label]) => ({ code, label, schreibweisen: roleLabelSpellings(label) })),
  status: STATUS_CODES.map((code) => {
    const label = LIFECYCLE_LABELS[code] ?? code;
    return { code, label, schreibweisen: [label] };
  }),
};

function istLeerraum(zeichen: string | undefined): boolean {
  return zeichen !== undefined && LEERRAUM.test(zeichen);
}

/** Das Wort um den Cursor: Anfang bis Leerraum oder `(`, Ende bis Leerraum oder `)`. */
function wortAm(text: string, cursor: number): Span {
  let von = cursor;
  while (von > 0 && !istLeerraum(text[von - 1]) && text[von - 1] !== '(') von -= 1;
  let bis = cursor;
  while (bis < text.length && !istLeerraum(text[bis]) && text[bis] !== ')') bis += 1;
  return { from: von, to: bis };
}

/** Die offene Werteliste `feld:(` vor dem Cursor, oder nichts. */
function offeneListe(text: string, cursor: number): { readonly feld: FieldDefinition; readonly klammer: number } | undefined {
  const vorher = text.slice(0, cursor);
  const klammer = vorher.lastIndexOf('(');
  if (klammer < 1 || vorher.slice(klammer).includes(')') || text[klammer - 1] !== ':') return undefined;
  let start = klammer - 1;
  while (start > 0 && !istLeerraum(text[start - 1])) start -= 1;
  const name = text.slice(start, klammer - 1).replace(/^-/u, '');
  const feld = fieldByName(name);
  return feld === undefined ? undefined : { feld, klammer };
}

function werteOptionen(feld: FieldDefinition, praefix: string, stelle: Span, nicht = false): LokaleOption[] {
  // S1 nimmt den Zustand nur als Ausschluss `-status:inaktiv` an (D1); jeder
  // andere Zustandswert fuehrte in einen Fehler (Codex an #581).
  const werte = feld.key === 'status' ? (nicht ? LOKALE_WERTE.status?.filter((wert) => wert.code === 'inactive') : undefined) : nicht ? undefined : LOKALE_WERTE[feld.key];
  if (werte === undefined) return [];
  const gesucht = fold(praefix);
  return werte
    .filter((wert) => wert.schreibweisen.some((schreibweise) => fold(schreibweise).startsWith(gesucht)))
    // Ohne zweite Spalte: alle Werte gehoeren zu demselben Feld (DESIGN P3 zu #581).
    .map((wert) => ({ id: `wert-${feld.key}-${wert.code}`, titel: wert.label, ersetzung: { stelle, text: `${printValue(wert.label)} ` } }));
}

function feldOptionen(text: string, cursor: number, wort: string, stelle: Span, explizit: boolean): LokaleOption[] {
  const nicht = wort.startsWith('-') ? '-' : '';
  // Steht hinter dem Cursor noch der Rest einer Angabe (`sti|chwort:Presse`),
  // wird nur der Name bis zum Doppelpunkt ersetzt, der Wert bleibt (Codex an #581).
  const rest = text.slice(cursor, stelle.to);
  const nameEnde = rest.indexOf(':');
  const namensStelle = nameEnde < 0 ? stelle : { from: stelle.from, to: cursor + nameEnde + 1 };
  const gesucht = fold(wort.slice(nicht.length));
  if (!explizit && gesucht.length < FELD_MINDESTLAENGE) return [];
  const treffer: LokaleOption[] = [];
  for (const feld of felder()) {
    // Ausschliessen laesst sich heute nur der Zustand (S1, D1).
    if (nicht !== '' && feld.key !== 'status') continue;
    // Der kanonische Name zuerst; ein Alias nur, wenn er allein passt („verl“ → verlag).
    const name = feld.names.find((kandidat) => kandidat.startsWith(gesucht));
    if (name === undefined) continue;
    // Den Zustand gibt es nur als Ausschluss (`-status:inaktiv`); so steht er auch in der Liste (DESIGN P3).
    const vorne = feld.key === 'status' ? '-' : nicht;
    treffer.push({ id: `feld-${feld.key}`, titel: `${vorne}${name}:`, zusatz: feld.label, ersetzung: { stelle: namensStelle, text: `${vorne}${name}:` } });
  }
  // Von selbst nur bei einem eindeutigen Feld; sonst flackerte die Liste bei Namen.
  if (!explizit && treffer.length !== 1) return [];
  // Ein schon vollstaendiger Name ohne Doppelpunkt („ort“) bleibt ein Vorschlag:
  // „Ort“ kann ein Name sein, und erst der Doppelpunkt macht ihn zur Angabe.
  return treffer;
}

/** Die Angabe direkt vor dem Cursor (`feld:wert ` oder `feld:wert`), wenn ihr Feld mehrere Werte traegt. */
function angabeDavor(text: string, cursor: number): { readonly stelle: Span; readonly feldTeil: string; readonly wert: string } | undefined {
  let bis = cursor;
  while (bis > 0 && text[bis - 1] === ' ') bis -= 1;
  // Ein Wert in Anfuehrungszeichen darf Leerzeichen tragen: dann beginnt die
  // Angabe vor dem oeffnenden Zeichen (`rolle:"Ansprechpartner:in im Vertrieb"`).
  let von = text[bis - 1] === '"' ? text.lastIndexOf('"', bis - 2) : bis;
  if (von < 0) return undefined;
  while (von > 0 && !istLeerraum(text[von - 1])) von -= 1;
  const angabe = text.slice(von, bis);
  const doppelpunkt = angabe.indexOf(':');
  if (doppelpunkt <= 0 || angabe.startsWith('-')) return undefined;
  const roh = angabe.slice(doppelpunkt + 1);
  if (roh.length === 0 || roh.startsWith('(') || roh === '"') return undefined;
  if (fieldByName(angabe.slice(0, doppelpunkt))?.multiValued !== true) return undefined;
  // In der Werteliste liest der Parser einen Doppelpunkt nicht als Wertteil:
  // `rolle:(Autor:in ODER …)` scheitert, `rolle:("Autor:in" ODER …)` nicht (DESIGN P1 zu #581).
  const wert = roh.startsWith('"') ? roh : printValue(roh);
  return { stelle: { from: von, to: cursor }, feldTeil: angabe.slice(0, doppelpunkt + 1), wert };
}

/**
 * Was die Liste an der Stelle `cursor` anbietet, oder `undefined` fuer eine
 * geschlossene Liste. `explizit` heisst: Pfeil runter hat sie verlangt; dann
 * oeffnen auch Feldvorschlaege unter drei Zeichen und das ODER.
 */
export function vorschlagLage(text: string, cursor: number, explizit: boolean): VorschlagLage | undefined {
  const stichwort = wertAnfrageAn(text, cursor);
  if (stichwort !== undefined) return { art: 'stichwort', anfrage: stichwort };
  const stelle = wortAm(text, cursor);
  const wort = text.slice(stelle.from, cursor);
  if (wort.includes('"')) return undefined;
  const liste = (optionen: readonly LokaleOption[]): VorschlagLage | undefined => (optionen.length === 0 ? undefined : { art: 'liste', optionen });

  const ohneNicht = wort.replace(/^-/u, '');
  const doppelpunkt = ohneNicht.indexOf(':');
  if (doppelpunkt > 0) {
    const name = ohneNicht.slice(0, doppelpunkt);
    const feld = fieldByName(name);
    const wertStelle = { from: stelle.from + (wort.length - ohneNicht.length) + doppelpunkt + 1, to: stelle.to };
    if (feld !== undefined) return liste(werteOptionen(feld, text.slice(wertStelle.from, cursor), wertStelle, wort.startsWith('-')));
    if (isLaterField(name)) return undefined;
    const aehnlich = similarFieldName(name);
    if (aehnlich === undefined) return undefined;
    const nameAnfang = stelle.from + (wort.length - ohneNicht.length);
    // Ersetzt wird nur der Name; was hinter dem Doppelpunkt schon steht, bleibt samt Cursor.
    const rest = text.slice(nameAnfang + name.length, cursor);
    // Der Haupttext ist, was eingesetzt wird; so sieht man vor Enter, was passiert (DESIGN P2 zu #581).
    return liste([{ id: 'meintest-feld', titel: aehnlich + rest, zusatz: MEINTEST, ersetzung: { stelle: { from: nameAnfang, to: cursor }, text: aehnlich + rest } }]);
  }

  // Ein kleines `oder`/`und` direkt hinter einer Angabe (E3).
  const gross = { oder: 'ODER', und: 'UND' }[wort];
  if (gross !== undefined && /\S:\S+\s+$/u.test(text.slice(0, stelle.from))) {
    return liste([{ id: 'meintest-operator', titel: gross, zusatz: MEINTEST, ersetzung: { stelle, text: `${gross} ` } }]);
  }

  const offen = offeneListe(text, cursor);
  if (offen !== undefined) {
    // Nur Felder mit mehreren Werten tragen eine Werteliste (S1); in `ort:(` gibt es nichts anzubieten.
    if (!offen.feld.multiValued) return undefined;
    const inDerKlammer = text.slice(offen.klammer + 1, cursor);
    // Hinter einem fertigen Wert folgt ODER, kein zweiter Wert ohne Verbindung;
    // es erscheint auf Pfeil runter (Codex an #581).
    if (wort.length === 0 && /[^\s(]\s+$/u.test(inDerKlammer) && !/(^|\s)ODER\s+$/u.test(inDerKlammer)) {
      return explizit ? liste([{ id: 'oder', titel: 'ODER', zusatz: 'noch ein Wert', ersetzung: { stelle, text: 'ODER ' } }]) : undefined;
    }
    // Im Stichwort-Wert der Liste fragt S0 wie ausserhalb (`stichwort:(Presse ODER Ly|)`).
    if (offen.feld.key === 'stichwort') {
      return wort.trim().length === 0 ? undefined : { art: 'stichwort', anfrage: { praefix: wort, wert: stelle } };
    }
    // Was schon in der Klammer steht, kommt nicht noch einmal (DESIGN P3 zu #581),
    // vor und hinter dem Cursor bis zur schliessenden Klammer (Codex an #609).
    const zu = text.indexOf(')', stelle.to);
    const schonDa = new Set(
      [text.slice(offen.klammer + 1, stelle.from), text.slice(stelle.to, zu < 0 ? text.length : zu)]
        .join(' ODER ')
        .split(/\s+ODER\s+|^\s*ODER\s+|\s+ODER\s*$/u)
        .map((teil) => fold(teil.trim().replace(/^"|"$/gu, '')))
        .filter((teil) => teil.length > 0),
    );
    const werte = LOKALE_WERTE[offen.feld.key] ?? [];
    return liste(
      werteOptionen(offen.feld, wort, stelle).filter((option) => {
        const wert = werte.find((kandidat) => option.id === `wert-${offen.feld.key}-${kandidat.code}`);
        // Auch der Code (`author`) gilt als Schreibweise, wie in roleCodeFor (Codex an #609).
        return wert === undefined || ![wert.code, ...wert.schreibweisen].some((schreibweise) => schonDa.has(fold(schreibweise)));
      }),
    );
  }

  const optionen: LokaleOption[] = [];
  if (wort.length === 0 && explizit) {
    const davor = angabeDavor(text, cursor);
    if (davor !== undefined) {
      // `stichwort:Presse` wird `stichwort:(Presse ODER |)`: S1 nimmt ODER nur in der Werteliste an.
      optionen.push({ id: 'oder', titel: 'ODER', zusatz: 'noch ein Wert', ersetzung: { stelle: davor.stelle, text: `${davor.feldTeil}(${davor.wert} ODER )`, cursorVorEnde: 1 } });
    }
  }
  optionen.push(...feldOptionen(text, cursor, wort, stelle, explizit));
  return liste(optionen);
}

/** Setzt eine lokale Option ein und nennt die neue Cursorstelle. */
export function ersetzen(text: string, ersetzung: Ersetzung): { readonly text: string; readonly cursor: number } {
  const danach = text.slice(ersetzung.stelle.to);
  // Kein zweites Leerzeichen und keins vor der schliessenden Klammer.
  const eingesetzt = ersetzung.text.endsWith(' ') && /^[\s)]/u.test(danach) ? ersetzung.text.slice(0, -1) : ersetzung.text;
  const davor = text.slice(0, ersetzung.stelle.from) + eingesetzt;
  return { text: davor + danach, cursor: davor.length - (ersetzung.cursorVorEnde ?? 0) };
}
